import type { AgentScreen } from './screenIndex';

/**
 * **모델이 쓴 말에서 우리끼리 쓰는 말을 걷어낸다.**
 *
 * 실측(2026-09-02, 걸음 벤치): 사람에게 이렇게 나갔다.
 *
 * > 현재 제공된 **화면 계약**에는 기본정보 표(**staffList**)만 있어, 이미 저장된
 * > 시험사원의 자격증 행을 입력할 수 있는 표가 없습니다.
 *
 * 읽는 사람은 회사 사무직이다. `화면 계약` 도 `staffList` 도 그에게는 아무 말이 아니다.
 * 오류 문구에는 이미 이런 거르개가 있었는데(`index.ts` 의 `named`), **모델이 쓴 말에는
 * 없었다.**
 *
 * 뜻을 바꾸지 않는다. 아는 이름만 갈아 끼우고, 모르는 것은 그대로 둔다 — 지우면
 * 문장이 무너지고, 무너진 문장은 아무것도 안 알려 준다.
 */
const JARGON: [RegExp, string][] = [
  [/화면\s*계약/g, '화면 정보'],
  [/rowsStateKey|stateKey|상태\s*키/g, '표 이름'],
  [/pageState\(?\)?|화면\s*상태값/g, '화면 상태'],
  [/draftFields?/g, '입력 칸'],
];

/** 내부 이름 → 화면에 적힌 이름. 긴 것부터 바꾼다 — 짧은 것이 긴 것의 일부일 수 있다. */
function namesOf(screen: AgentScreen | undefined): [string, string][] {
  if (!screen) return [];
  const pairs: [string, string][] = [];
  for (const tab of screen.tabs ?? []) {
    if (tab.stateKey && tab.title) pairs.push([tab.stateKey, tab.title]);
  }
  for (const field of [...(screen.draftFields ?? []), ...(screen.fields ?? [])]) {
    if (field.label?.trim()) pairs.push([field.key, field.label.trim()]);
  }
  // 목록 표에는 화면에 적힌 이름이 없다. 사람은 그것을 **그 화면의 목록**으로 부른다.
  const list = screen.grids?.[0]?.key;
  if (list) pairs.push([list, `${screen.name} 목록`]);
  return pairs
    .filter(([key, name]) => key && name && key !== name)
    .sort((a, b) => b[0].length - a[0].length);
}

export function plainSpeech(text: string, screen?: AgentScreen): string {
  let said = text;
  // **백틱에 싼 표 칸 id 는 칸 이름으로.** 계산 결과를 설명하며 「`distance`가 0보다 큰 출근」이라고 썼다
  // (일근태등록, 2026-10-21). 백틱 없이 쓴 낱말은 우연히 같은 영어일 수 있어 두고, 싼 것만 바꾼다.
  const columns = new Map((screen?.grids ?? []).flatMap((grid) => grid.columns)
    .filter((column) => column.text?.trim() && column.text !== column.id)
    .map((column) => [column.id, column.text.trim()] as const));
  said = said.replace(/`([A-Za-z_][A-Za-z0-9_]*)`/g, (whole, id: string) => (
    columns.has(id) ? `「${columns.get(id)}」` : whole));
  for (const [key, name] of namesOf(screen)) {
    // **낱말 경계에서만 바꾼다.** `data` 가 `dataMaterials` 안에 들어 있어, 아무 데서나
    // 바꾸면 남의 이름을 반쪽만 갈아 더 알 수 없는 말이 된다.
    said = said.replace(new RegExp(`(^|[^A-Za-z0-9_])${key}(?![A-Za-z0-9_])`, 'g'), `$1${name}`);
  }
  for (const [pattern, plain] of JARGON) said = said.replace(pattern, plain);
  // 갈아 끼운 뒤 `표(인사등록 목록)` 처럼 겹치는 자리가 생긴다. 괄호 안이 앞말과 같으면 뗀다.
  return said.replace(/([^\s(]+)\s*\(\1\)/g, '$1');
}
